import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { wireQuotationsAPI, partsAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Plus, Trash2, FileDown, Loader2, Cable } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n, ccy = 'INR', dec = 2) => {
  const sym = ccy === 'INR' ? '₹' : ccy === 'USD' ? '$' : ccy === 'EUR' ? '€' : `${ccy} `;
  return `${sym}${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
};
const fmtDate = (s) => { if (!s) return '-'; try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; } };
const statusColor = (s) => {
  const x = String(s || '').toLowerCase();
  if (['approved', 'won', 'released'].includes(x)) return 'bg-emerald-100 text-emerald-700';
  if (['rejected', 'lost'].includes(x)) return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-700';
};

function AddLineItemDialog({ open, onOpenChange, quotation, onAdded }) {
  const [wireParts, setWireParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [activeCosting, setActiveCosting] = useState(null);
  const [item, setItem] = useState({
    no_of_coils: 1,
    coil_weight_kg: 1000,
    total_kg: 1000,
    quantity_unit: 'kg',
    price_unit: 1,           // 1 = per kg, 1000 = per MT
    unit_price: 0,
    description_override: '',
    remarks: '',
  });
  const [lastEdit, setLastEdit] = useState(null);   // 'coils' | 'total'
  const [saving, setSaving] = useState(false);

  // Load wire parts on open
  useEffect(() => {
    if (open && wireParts.length === 0) {
      partsAPI.list({ product_category: 'wire', limit: 500 })
        .then((r) => setWireParts(r.data.items || r.data.data || []))
        .catch(() => toast.error('Failed to load wire parts'));
    }
  }, [open]);  // eslint-disable-line

  // Smart link: editing coils → total_kg = coils × coil_wt
  //            editing total_kg → coils = total_kg / coil_wt
  useEffect(() => {
    if (lastEdit === 'coils') {
      const t = (parseFloat(item.no_of_coils) || 0) * (parseFloat(item.coil_weight_kg) || 0);
      setItem((it) => ({ ...it, total_kg: t > 0 ? +t.toFixed(2) : it.total_kg }));
    } else if (lastEdit === 'total') {
      const cw = parseFloat(item.coil_weight_kg) || 0;
      const t = parseFloat(item.total_kg) || 0;
      if (cw > 0) setItem((it) => ({ ...it, no_of_coils: +(t / cw).toFixed(4) }));
    }
  }, [item.no_of_coils, item.total_kg, item.coil_weight_kg, lastEdit]);

  const pickPart = async (part) => {
    setSelectedPart(part);
    setItem((it) => ({
      ...it,
      coil_weight_kg: part.wire_coil_weight_kg || 1000,
    }));
    setLastEdit('coils');
    // Try to fetch active wire costing for this part to pre-fill price.
    // Wire costings are always in INR — convert to quotation currency using
    // the quotation's exchange_rate (price_in_inr ÷ exchange_rate = price_in_qcc).
    try {
      const r = await api.get('/wire-costings', { params: { search: part.internal_part_no } });
      const list = (r.data.items || []).filter((c) => c.part_id === part.id);
      const active = list.find((c) => c.is_active) || list[0];
      if (active) {
        setActiveCosting(active);
        const spInr = active.selling_price_per_kg || 0;
        const xr = parseFloat(quotation?.exchange_rate) || 1;
        const priceInQcc = (quotation?.currency || 'INR') === 'INR' ? spInr : spInr / xr;
        setItem((it) => ({ ...it, unit_price: +priceInQcc.toFixed(4) }));
      }
    } catch { /* ok */ }
  };

  const lineTotal = (() => {
    const t = parseFloat(item.total_kg) || 0;
    const p = parseFloat(item.unit_price) || 0;
    const u = parseInt(item.price_unit) || 1;
    return (t * p) / u;
  })();

  const handleAdd = async () => {
    if (!selectedPart) { toast.error('Pick a wire part'); return; }
    setSaving(true);
    try {
      await wireQuotationsAPI.addItem(quotation.id, {
        part_id: selectedPart.id,
        costing_id: activeCosting?.id || null,
        no_of_coils: parseFloat(item.no_of_coils) || 0,
        coil_weight_kg: parseFloat(item.coil_weight_kg) || 0,
        total_kg: parseFloat(item.total_kg) || 0,
        quantity_unit: item.quantity_unit,
        price_unit: parseInt(item.price_unit) || 1,
        unit_price: parseFloat(item.unit_price) || 0,
        description_override: item.description_override || null,
        remarks: item.remarks || null,
      });
      toast.success('Line item added');
      onOpenChange(false);
      // Reset
      setSelectedPart(null);
      setActiveCosting(null);
      setItem({ no_of_coils: 1, coil_weight_kg: 1000, total_kg: 1000, quantity_unit: 'kg', price_unit: 1, unit_price: 0, description_override: '', remarks: '' });
      if (onAdded) onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add item');
    } finally {
      setSaving(false);
    }
  };

  const ccy = quotation?.currency || 'INR';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="add-line-dialog">
        <DialogHeader>
          <DialogTitle>Add Wire Line Item</DialogTitle>
          <DialogDescription>Pick a wire part. Coils ↔ Total Kg auto-sync as you type.</DialogDescription>
        </DialogHeader>

        {!selectedPart ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {wireParts.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No wire parts yet — go to Wire Parts to add one.</p>
            ) : wireParts.map((p) => (
              <button
                key={p.id}
                onClick={() => pickPart(p)}
                className="w-full text-left p-3 rounded-md hover:bg-muted/60 border border-transparent hover:border-border flex items-center justify-between"
                data-testid={`wql-pick-${p.id}`}
              >
                <div>
                  <p className="font-mono font-medium">{p.internal_part_no}</p>
                  <p className="text-xs text-muted-foreground">{p.description}</p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>{p.wire_diameter_min_mm} – {p.wire_diameter_max_mm} mm</p>
                  <p>{p.wire_grade}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-mono font-semibold">{selectedPart.internal_part_no}</p>
                  <p className="text-xs text-muted-foreground">{selectedPart.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedPart.wire_diameter_min_mm} – {selectedPart.wire_diameter_max_mm} mm · {selectedPart.wire_grade}
                    {activeCosting && (
                      <span className="ml-2 text-emerald-700">
                        · costing {activeCosting.costing_number} @ ₹{Number(activeCosting.selling_price_per_kg).toFixed(4)}/kg INR
                        {quotation?.currency !== 'INR' && quotation?.exchange_rate && (
                          <span className="ml-1 text-muted-foreground">
                            (÷ {quotation.exchange_rate} = {fmt(activeCosting.selling_price_per_kg / quotation.exchange_rate, quotation.currency, 4)})
                          </span>
                        )}
                      </span>
                    )}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedPart(null); setActiveCosting(null); }}>Change</Button>
              </div>
            </div>

            <div>
              <Label>Description (override, optional)</Label>
              <Input value={item.description_override} onChange={(e) => setItem({ ...item, description_override: e.target.value })} placeholder={selectedPart.description} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>No. of Coils</Label>
                <Input type="number" step="0.01" value={item.no_of_coils}
                  onChange={(e) => { setLastEdit('coils'); setItem({ ...item, no_of_coils: e.target.value }); }}
                  data-testid="wql-no-coils" />
              </div>
              <div>
                <Label>Coil Weight (Kg)</Label>
                <Input type="number" step="0.01" value={item.coil_weight_kg}
                  onChange={(e) => { setLastEdit('coils'); setItem({ ...item, coil_weight_kg: e.target.value }); }}
                  data-testid="wql-coil-wt" />
              </div>
              <div>
                <Label>Total Weight (Kg)</Label>
                <Input type="number" step="0.01" value={item.total_kg}
                  onChange={(e) => { setLastEdit('total'); setItem({ ...item, total_kg: e.target.value }); }}
                  data-testid="wql-total-kg" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Quantity UoM</Label>
                <Select value={item.quantity_unit} onValueChange={(v) => setItem({ ...item, quantity_unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kg</SelectItem>
                    <SelectItem value="mt">MT (Ton)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price per</Label>
                <Select value={String(item.price_unit)} onValueChange={(v) => setItem({ ...item, price_unit: parseInt(v) })}>
                  <SelectTrigger data-testid="wql-price-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Per Kg</SelectItem>
                    <SelectItem value="1000">Per MT (Ton)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Unit Price ({ccy} / {item.price_unit === 1 ? 'Kg' : 'MT'})</Label>
                <Input type="number" step="0.0001" value={item.unit_price} onChange={(e) => setItem({ ...item, unit_price: e.target.value })} data-testid="wql-unit-price" />
              </div>
            </div>

            <Card className="bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200">
              <CardContent className="p-3 flex justify-between items-center">
                <div className="text-sm">
                  <span className="text-muted-foreground">Total:</span> <span className="font-mono font-bold ml-1">{Number(item.total_kg || 0).toLocaleString('en-IN')} Kg</span>
                  <span className="text-muted-foreground ml-3">× {fmt(item.unit_price, ccy, 4)} / {item.price_unit === 1 ? 'Kg' : 'MT'}</span>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase font-semibold text-emerald-700">Line Total</p>
                  <p className="text-xl font-extrabold font-mono text-emerald-700" data-testid="wql-line-total">{fmt(lineTotal, ccy)}</p>
                </div>
              </CardContent>
            </Card>

            <div>
              <Label>Remarks</Label>
              <Input value={item.remarks} onChange={(e) => setItem({ ...item, remarks: e.target.value })} placeholder="optional" />
            </div>
          </div>
        )}

        {selectedPart && (
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} className="btn-primary" data-testid="wql-add-btn">
              {saving ? 'Adding…' : 'Add Line'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function WireQuotationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    wireQuotationsAPI.get(id)
      .then((r) => setQ(r.data))
      .catch(() => { toast.error('Failed to load wire quotation'); navigate('/wire/quotations'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const removeItem = async (itemId) => {
    if (!window.confirm('Remove this line item?')) return;
    try {
      await wireQuotationsAPI.deleteItem(id, itemId);
      toast.success('Line removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading || !q) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const ccy = q.currency || 'INR';
  const items = q.items || [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wire-quotation-detail">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate('/wire/quotations')}><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="text-3xl font-heading font-bold uppercase tracking-tight flex items-center gap-2">
              {q.quotation_number}
              <Cable className="h-5 w-5 text-blue-500" />
              {q.version_number > 1 && <span className="text-muted-foreground text-base">v{q.version_number}</span>}
            </h1>
            <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
              <span className="font-medium">{q.customer_name}</span>
              <Badge className={statusColor(q.status)} variant="outline">{q.status}</Badge>
              <span>· {q.currency}</span>
              <span>· Valid till {fmtDate(q.validity_date)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate(`/wire/quotations/${q.id}/print`)} data-testid="view-pdf-btn">
            <FileDown className="h-4 w-4 mr-1.5" />View / PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base uppercase tracking-wide">Line Items ({items.length})</CardTitle>
          {q.status === 'draft' && (
            <Button size="sm" className="btn-primary" onClick={() => setAddOpen(true)} data-testid="add-wire-line-btn"><Plus className="h-4 w-4 mr-1.5" />Add Wire Item</Button>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead>Part</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Diameter</TableHead>
                <TableHead>Grade</TableHead>
                <TableHead className="text-right">Coil Wt</TableHead>
                <TableHead className="text-right">Coils</TableHead>
                <TableHead className="text-right">Total Kg</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Line Total</TableHead>
                {q.status === 'draft' && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it, idx) => (
                <TableRow key={it.id} data-testid={`wq-item-${it.id}`}>
                  <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                  <TableCell className="font-mono text-xs">{it.part_internal_no}</TableCell>
                  <TableCell className="max-w-xs truncate">{it.description}</TableCell>
                  <TableCell className="font-mono text-xs">{it.wire_diameter_min_mm} – {it.wire_diameter_max_mm} mm</TableCell>
                  <TableCell>{it.wire_grade}</TableCell>
                  <TableCell className="text-right font-mono">{Number(it.coil_weight_kg || 0).toLocaleString('en-IN')} kg</TableCell>
                  <TableCell className="text-right font-mono">{Number(it.no_of_coils || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{Number(it.total_kg || 0).toLocaleString('en-IN')} kg</TableCell>
                  <TableCell className="text-right font-mono">{fmt(it.unit_price, ccy, 4)} / {it.price_unit === 1 ? 'Kg' : 'MT'}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{fmt(it.line_total, ccy)}</TableCell>
                  {q.status === 'draft' && (
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => removeItem(it.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={q.status === 'draft' ? 11 : 10} className="text-center text-muted-foreground py-6">No items yet. Click <strong>Add Wire Item</strong>.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Totals */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-end">
            <div className="text-right">
              <p className="text-sm text-muted-foreground uppercase tracking-wide">Quotation Total</p>
              <p className="text-3xl font-extrabold font-mono">{fmt(q.total_amount || q.subtotal, ccy)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <AddLineItemDialog open={addOpen} onOpenChange={setAddOpen} quotation={q} onAdded={load} />
    </div>
  );
}
